import { format, parseISO } from 'date-fns';
import { Badge } from '@/components/ui/badge';

// ── Formatting helpers shared across all DSR section components ──

export function formatTimestamp(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return format(parseISO(iso), "MMM d, yyyy 'at' HH:mm");
  } catch {
    return iso;
  }
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return format(parseISO(iso), 'MMM d, yyyy');
  } catch {
    return iso;
  }
}

export function formatTime(time: string | null | undefined): string {
  if (!time) return '—';
  // Time might be HH:MM:SS+00 or ISO timestamp
  if (time.includes('T')) return formatTimestamp(time);
  return time.substring(0, 5); // HH:MM
}

export function BoolBadge({ value }: { value: boolean | null | undefined }) {
  if (value == null) return <span className="text-muted-foreground">—</span>;
  return value ? (
    <Badge className="bg-mozaic-green/15 text-mozaic-green">Yes</Badge>
  ) : (
    <Badge className="bg-muted text-muted-foreground">No</Badge>
  );
}

const RISK_COLORS: Record<string, string> = {
  low: 'bg-mozaic-green/15 text-mozaic-green',
  medium: 'bg-mozaic-orange/15 text-mozaic-orange',
  high: 'bg-mozaic-red/15 text-mozaic-red',
  critical: 'bg-mozaic-red/20 text-mozaic-red font-bold',
};

export function RiskBadge({ level }: { level: string | null | undefined }) {
  if (!level) return <span className="text-muted-foreground">—</span>;
  return (
    <Badge className={RISK_COLORS[level] ?? 'bg-muted text-muted-foreground'}>
      {level.charAt(0).toUpperCase() + level.slice(1)}
    </Badge>
  );
}

const STATUS_COLORS: Record<string, string> = {
  planned: 'bg-mozaic-blue/15 text-mozaic-blue',
  in_progress: 'bg-mozaic-orange/15 text-mozaic-orange',
  completed: 'bg-mozaic-green/15 text-mozaic-green',
  cancelled: 'bg-muted text-muted-foreground',
  deferred: 'bg-muted text-muted-foreground',
  open: 'bg-mozaic-orange/15 text-mozaic-orange',
  active: 'bg-mozaic-green/15 text-mozaic-green',
  suspended: 'bg-mozaic-red/15 text-mozaic-red',
  closed: 'bg-muted text-muted-foreground',
};

export function StatusBadge({ status }: { status: string | null | undefined }) {
  if (!status) return <span className="text-muted-foreground">—</span>;
  const label = status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  return (
    <Badge className={STATUS_COLORS[status] ?? 'bg-muted text-muted-foreground'}>
      {label}
    </Badge>
  );
}

export function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="space-y-0.5">
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="text-sm">{value ?? <span className="text-muted-foreground">—</span>}</dd>
    </div>
  );
}

export function SectionHeading({
  id,
  title,
  count,
}: {
  id: string;
  title: string;
  count?: number;
}) {
  return (
    <h2 id={id} className="scroll-mt-20 text-base font-semibold">
      {title}
      {count != null && count > 0 && (
        <Badge className="ml-2 bg-mozaic-navy/10 text-mozaic-navy">{count}</Badge>
      )}
    </h2>
  );
}

export function EmptySection({ name }: { name: string }) {
  return (
    <p className="py-4 text-sm text-muted-foreground">
      No {name} recorded for this report.
    </p>
  );
}

export function TagList({ tags }: { tags: string[] | null | undefined }) {
  if (!tags || tags.length === 0) return <span className="text-muted-foreground">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {tags.map((tag) => (
        <Badge key={tag} variant="outline" className="text-xs">
          {tag.replace(/_/g, ' ')}
        </Badge>
      ))}
    </div>
  );
}
